/**
 * Reading a rider projection record.
 *
 * The projection stores are typed as `Record<string, unknown>`: the field names
 * come from the server's projection allowlists, and the demo seed writes a
 * subset of them. So every rider surface reads fields defensively rather than
 * trusting a compile-time shape, and these helpers are the single place that
 * knowledge lives. They were duplicated between the dash home and the rider
 * pages, which is how the two drifted apart (see routeLine).
 */
import type { TenantProjectionStore } from '@ovasabi/frontend-kit'
import {
  prototypeStoreRegistry,
  useRiderProfileSnapshot,
} from '../../generated/prototypeRuntime'

export type Snapshot = { records: readonly Record<string, unknown>[] }

type Store = TenantProjectionStore<Record<string, unknown>>

/**
 * The prototype store, for the offline lane only.
 *
 * Under a live runtime the projection socket owns these records and writing to
 * the store would be overwritten by the next delta. Every call site checks
 * `isLiveRuntime()` first.
 */
export const getStore = (key: string): Store | undefined =>
  prototypeStoreRegistry.get(key) as unknown as Store | undefined

/** The first non-empty value among `keys`, or `fallback`. */
export const text = (
  record: Record<string, unknown>,
  keys: readonly string[],
  fallback = '',
): string => {
  for (const key of keys) {
    const value = record[key]
    if (value !== undefined && value !== null && String(value).trim()) return String(value)
  }
  return fallback
}

/** The first value among `keys` that parses as a finite number. */
export const number = (
  record: Record<string, unknown>,
  keys: readonly string[],
  fallback = 0,
): number => {
  const parsed = Number(text(record, keys, String(fallback)))
  return Number.isFinite(parsed) ? parsed : fallback
}

/** The scheduled start, when the order is scheduled rather than immediate. */
export const schedule = (record: Record<string, unknown>): string | undefined =>
  text(record, ['scheduled_at']) || undefined

/**
 * The pickup → drop-off line.
 *
 * `pickup_address` / `dropoff_address` are the names the server projects
 * (see internal/startup/projection_fetcher.go, deliveries allowlist), and they
 * are tried first. The bare `pickup` / `dropoff` keys are the pre-projection
 * shape; they are still accepted so an older seed or a cached record renders
 * instead of showing the fallback text.
 */
export const routeLine = (record: Record<string, unknown>): string => {
  const pickup = text(record, ['pickup_address', 'pickup'], 'Pickup location')
  const dropoff = text(record, ['dropoff_address', 'dropoff'], 'Customer address')
  return `${pickup} → ${dropoff}`
}

/** Distance to pickup, in km. Meter-keyed records are converted. */
export const pickupKm = (record: Record<string, unknown>): number => {
  const km = number(record, ['pickup_distance_km'])
  if (km > 0) return km
  return number(record, ['pickup_distance_meters']) / 1000
}

const TERMINAL_STAGES = ['done', 'cancelled']

/** The rider's in-flight delivery, if there is one. */
export const activeDelivery = (
  records: readonly Record<string, unknown>[],
): Record<string, unknown> | undefined =>
  records.find((record) => !TERMINAL_STAGES.includes(text(record, ['stage', 'status'])))

/** This rider's id, falling back to the demo seed's. */
export const useRiderId = (): string => {
  const profiles = useRiderProfileSnapshot() as Snapshot
  return text(profiles.records[0] ?? {}, ['id'], 'rider-prototype')
}
